/**
 * Media Tab Content Component
 * Wraps MediaGrid/FilesList/LinksList for use in the right sidebar (compact 320px layout)
 */

import React, { useState } from 'react';
import { useMediaHistoryTabs } from '@/hooks/useSearch';
import { MediaGrid } from '@/components/media/MediaGrid';
import { FilesList } from '@/components/media/FilesList';
import { LinksList } from '@/components/media/LinksList';
import type { SidebarTab } from './RightSidebarTabs';

interface MediaTabContentProps {
  tab: Exclude<SidebarTab, 'members'>;
  channelId?: string | null;
  dmThreadId?: string | null;
}

export const MediaTabContent: React.FC<MediaTabContentProps> = ({ tab, channelId, dmThreadId }) => {
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Reset offset when tab changes
  React.useEffect(() => {
    setOffset(0);
  }, [tab]);

  const scope = {
    channelId: channelId || undefined,
    dmThreadId: dmThreadId || undefined,
  };

  const { data, isLoading, isError } = useMediaHistoryTabs(
    scope,
    tab === 'links' ? 'links' : tab === 'files' ? 'files' : 'media',
    { limit, offset }
  );

  const handleLoadMore = () => {
    if (data?.pagination.hasMore) {
      setOffset((prev) => prev + limit);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-gray-300 dark:border-dark-border-secondary border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-sm text-red-500 dark:text-red-400">
        Грешка при зареждане
      </div>
    );
  }

  if (!data || data.results.length === 0) {
    const emptyMessages: Record<string, string> = {
      media: 'Няма споделена медия',
      files: 'Няма споделени файлове',
      links: 'Няма споделени линкове',
    };

    return (
      <div className="text-center py-8 text-sm text-gray-400 dark:text-dark-text-muted">
        {emptyMessages[tab]}
      </div>
    );
  }

  return (
    <div className="p-2 sidebar-media-content">
      {tab === 'media' && <MediaGrid messages={data.results} scope={scope} />}
      {tab === 'files' && <FilesList messages={data.results} scope={scope} />}
      {tab === 'links' && <LinksList messages={data.results} scope={scope} />}

      {data.pagination.hasMore && (
        <div className="flex justify-center mt-3">
          <button
            onClick={handleLoadMore}
            className="px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            Зареди още
          </button>
        </div>
      )}

      {/* Compact overrides for sidebar width */}
      <style>{`
        .sidebar-media-content .media-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 0.5rem !important;
        }
        .sidebar-media-content .media-thumbnail {
          height: 120px !important;
        }
        .sidebar-media-content .media-info {
          padding: 0.5rem !important;
        }
        .sidebar-media-content .media-sender {
          font-size: 0.75rem !important;
        }
        .sidebar-media-content .media-date {
          font-size: 0.625rem !important;
        }
        .sidebar-media-content .file-card {
          padding: 0.75rem !important;
          gap: 0.5rem !important;
        }
        .sidebar-media-content .file-icon-container {
          width: 2.25rem !important;
          height: 2.25rem !important;
        }
        .sidebar-media-content .file-icon {
          font-size: 1.25rem !important;
        }
        .sidebar-media-content .file-name {
          font-size: 0.8125rem !important;
        }
        .sidebar-media-content .file-info {
          font-size: 0.75rem !important;
        }
        .sidebar-media-content .file-meta {
          font-size: 0.6875rem !important;
        }
        .sidebar-media-content .file-actions {
          display: none !important;
        }
        .sidebar-media-content .file-card:hover .file-actions {
          display: flex !important;
        }
        .sidebar-media-content .link-card {
          padding: 0.75rem !important;
        }
        .sidebar-media-content .link-url {
          font-size: 0.75rem !important;
        }
        .sidebar-media-content .link-title {
          font-size: 0.8125rem !important;
        }
        .sidebar-media-content .link-description {
          font-size: 0.75rem !important;
          -webkit-line-clamp: 1 !important;
        }
        .sidebar-media-content .link-meta {
          font-size: 0.6875rem !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0.125rem !important;
        }
        .sidebar-media-content .link-message-content {
          display: none !important;
        }
      `}</style>
    </div>
  );
};
